import { useState, useEffect } from 'react';
import { api } from '../../lib/api.js';

// Pick the number you want by LOOKING at the dashboard, not by reading a list of
// tile names out of a dropdown.
//
// "Tickets sold" and "Tickets sold (paid)" are indistinguishable as two lines of
// a <select>; side by side with their live values — 12,480 against 9,155 — the
// one you meant is obvious. So this lays the dashboard's KPI tiles out as a grid
// of cards, each reading its real current value, and you tap the one you want.
// It's the same picture you'd get by opening the dashboard, without leaving the
// goal you're half-way through building.
//
// The dropdown stays: this is offered ALONGSIDE it, because a grid is the better
// way to recognise a tile and a list is the better way to find one you can already
// name. Values load one request per tile, in parallel, capped — a dashboard with
// forty KPIs shouldn't fire forty queries at a form that's still being filled in.
const MAX_TILES = 24;

export default function TileBrowser({ suiteId, dashboardId, tiles = [], value, onPick, unit, fmt }) {
  const [vals, setVals] = useState({}); // tileId → { value } | { error: true }

  useEffect(() => {
    if (!suiteId || !dashboardId || !tiles.length) return undefined;
    let alive = true;
    setVals({});
    // Fire them together but bounded; each lands on its own so the grid fills in
    // progressively rather than blocking on the slowest tile.
    for (const t of tiles.slice(0, MAX_TILES)) {
      api.goalTileValue(suiteId, dashboardId, t.tileId)
        .then((r) => { if (alive) setVals((v) => ({ ...v, [t.tileId]: { value: r?.value } })); })
        .catch(() => { if (alive) setVals((v) => ({ ...v, [t.tileId]: { error: true } })); });
    }
    return () => { alive = false; };
  }, [suiteId, dashboardId, tiles.map((t) => t.tileId).join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  const shown = tiles.slice(0, MAX_TILES);
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(132px, 1fr))', gap: 8 }}>
        {shown.map((t) => {
          const v = vals[t.tileId];
          const on = t.tileId === value;
          return (
            <button
              key={t.tileId}
              type="button"
              onClick={() => onPick(t.tileId)}
              style={{
                display: 'flex', flexDirection: 'column', gap: 4, minHeight: 68, padding: '10px 11px',
                border: `1.5px solid ${on ? 'var(--brand)' : 'var(--hairline)'}`,
                background: on ? 'rgba(var(--brand-rgb,10,132,255),0.08)' : 'var(--card)',
                borderRadius: 10, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit', color: 'var(--text)',
              }}
            >
              <span style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{t.title}</span>
              <span style={{ fontSize: 17, fontWeight: 800, lineHeight: 1.1 }}>
                {v == null ? <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--muted)' }}>reading…</span>
                  : v.error || v.value == null ? <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--muted)' }}>—</span>
                    : fmt(v.value, unit)}
              </span>
            </button>
          );
        })}
      </div>
      {tiles.length > MAX_TILES && (
        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 6 }}>
          Showing the first {MAX_TILES} of {tiles.length} — use the list above for the rest.
        </div>
      )}
    </div>
  );
}
